import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tap } from 'rxjs';

import { useAlarmLogListQuery, useAlarmLogRemoveAllQuery } from '@/hook';
import {
  Close,
  Done,
  Notifications,
  VBasePopover,
  VFlex,
  VStack,
  VTab,
  VTabContent,
  VTabTrigger,
  VTabTriggerContainer,
  VTextButton,
  new__cn as cn,
  useFlag,
  useSubscriptions,
} from '@/package';
import { VIconButton } from '@/package/kiska/src/component/UI/VIconButton';
import { useWebSocket } from '@/provider';
import { useNotificationStore } from '@/store';

import NotificationList from './NotificationList';
import { makeNotification } from './util';

const TAB_ENTRY_LIST = [
  ['0', 'alarm.alarmTabAllAlarm.siderology'],
  // ['1', 'Unread (NN)'],
  // ['2', 'Errors'],
  ['3', 'Track ID'],
] as const;

const ALARM_TYPE_BY_TAB_VALUE = {
  [TAB_ENTRY_LIST[0][0]]: '',
  [TAB_ENTRY_LIST[1][0]]: 'TrackIDUpdateRequest',
} as const;

const Notification = ({
  defaultTabValue = TAB_ENTRY_LIST[0][0],
  iconSize = 'lg',
}: {
  defaultTabValue?: (typeof TAB_ENTRY_LIST)[number][number];
  iconSize?: React.ComponentProps<typeof VIconButton>['size'];
}) => {
  const { alarmMessage$ } = useWebSocket();
  const { notificationRawList, addRawNotification, setRawNotificationList } =
    useNotificationStore();
  const {
    data: alarmLogList,
    isLoading: isAlarmLogListLoading,
    refetch,
  } = useAlarmLogListQuery();
  const { value: visible, setFalse: close, setTrue: open } = useFlag(false);

  useSubscriptions(
    () => [alarmMessage$.pipe(tap(addRawNotification)).subscribe()],
    [alarmLogList, alarmMessage$, notificationRawList],
  );

  useEffect(() => {
    if (isAlarmLogListLoading) return;

    setRawNotificationList(alarmLogList ?? []);
  }, [alarmLogList, isAlarmLogListLoading]);

  useEffect(() => {
    if (visible) return;

    refetch();
  }, [visible]);

  return (
    <VBasePopover
      placement="bottom-right"
      content={() => (
        <NotificationContent
          defaultTabValue={defaultTabValue}
          onClose={close}
        />
      )}
      className="rounded-md! shadow-none!"
      trigger="click"
      visible={visible}
      onUpdateVisible={v => (v ? open() : close())}
    >
      <VIconButton
        size={iconSize}
        variant="roundPrimary"
        Icon={Notifications}
      />
    </VBasePopover>
  );
};

const NotificationEmpty = () => {
  const { t } = useTranslation();

  return (
    <VFlex className="px w-full py-10" justify="center" items="center">
      <VStack gap={0.25} items="center">
        <Notifications className="h-7 w-7 text-gray-300" />
        <div className="text-body-xl text-gray-300">
          {t('alarm.emptyAlarmList.perplication')}
        </div>
      </VStack>
    </VFlex>
  );
};

const NotificationContent = ({
  defaultTabValue = TAB_ENTRY_LIST[0][0],
  onClose,
}: {
  defaultTabValue?: (typeof TAB_ENTRY_LIST)[number][number];
  onClose: () => void;
}) => {
  const notificationRawList = useNotificationStore(
    state => state.notificationRawList,
  );
  const { t } = useTranslation();
  const { mutateAsync: removeAllAlarmLog } = useAlarmLogRemoveAllQuery();
  const [alarmType, setAlarmType] = useState<string>('');

  const filteredNotificationRawList = useMemo(
    () =>
      alarmType
        ? notificationRawList.filter(n => n.alarmType === alarmType)
        : notificationRawList,
    [alarmType, notificationRawList],
  );
  const filteredNotificationLength = useMemo(
    () => filteredNotificationRawList.length,
    [filteredNotificationRawList.length],
  );
  const filteredNotificationList = useMemo(
    () => filteredNotificationRawList.map(makeNotification),
    [filteredNotificationRawList],
  );

  return (
    <VTab
      defaultValue={defaultTabValue}
      size="sm"
      onValueChange={value =>
        setAlarmType(ALARM_TYPE_BY_TAB_VALUE[value as '0' | '3'])
      }
    >
      <VStack gap={0.5} className="w-[424px] py-2">
        <VFlex className="w-full px-4" items="center" justify="between">
          <div className="text-headline-sm font-semibold">Notifications</div>
          <Close
            className="h-6 w-6 text-gray-400"
            role="button"
            onClick={onClose}
          />
        </VFlex>

        <VFlex className="w-full px-4" items="center" justify="between">
          <VTabTriggerContainer>
            {TAB_ENTRY_LIST.map(entry => (
              <VTabTrigger key={entry[0]} value={entry[0]}>
                {t(entry[1])}
              </VTabTrigger>
            ))}
          </VTabTriggerContainer>

          <VFlex
            className={cn({
              'cursor-pointer text-gray-500': filteredNotificationLength,
              'text-gray-300': !filteredNotificationLength,
            })}
            items="center"
            gap={0.25}
            onClick={() => filteredNotificationLength && removeAllAlarmLog()}
          >
            <Done
              className={cn('size-4', {
                'cursor-not-allowed': !filteredNotificationLength,
              })}
            />
            <VTextButton
              className={cn('text-body-lg font-semibold', {
                'cursor-not-allowed': !filteredNotificationLength,
              })}
            >
              {t('alarm.removeAll.toteload')}
            </VTextButton>
          </VFlex>
        </VFlex>

        {TAB_ENTRY_LIST.map(entry => {
          return (
            <VTabContent key={entry[0]} value={entry[0]}>
              {filteredNotificationLength ? (
                <NotificationList notificationList={filteredNotificationList} />
              ) : (
                <NotificationEmpty />
              )}
            </VTabContent>
          );
        })}
      </VStack>
    </VTab>
  );
};

export default Notification;
